/**
 * Wait for a value to be truthy and return it via the callback.
 *
 * @param check the function which returns the item we're waiting for
 * @param callback the callback that should be called when the item has been found
 * @param timeout the timeout function or value that is used to wait for the value
 */
export function waitFor<T>(
  check: () => T | null | undefined,
  callback: (result: T) => void,
  timeout: number | ((times: number) => number | false) = 1000,
) {
  let times = 0;

  const handle = () => {
    const result = check();
    if (!result) {
      times += 1;
      const ms = typeof timeout === "number" ? timeout : timeout(times);
      if (!ms) {
        return;
      }

      setTimeout(handle, ms);
      return;
    }

    callback(result);
    return;
  };

  handle();
}

/** A promisified timeout. */
export function wait(ms = 0): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function waitUntil(
  predicate: () => boolean,
  options?: { sleep?: number; timeout?: number },
): Promise<void> {
  const start = Date.now();
  const sleep = options?.sleep ?? 10;
  const timeout = options?.timeout ?? 1000;
  while (Date.now() - start < timeout) {
    if (predicate()) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, sleep));
  }
  throw new Error("Timed out waiting for condition");
}
